import React, { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Scale, Plus, Trash2, Link2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { fmtGbp } from "@/lib/constants";

const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };
const TYPES = ["Factual", "Judgemental", "Projected", "Qualitative"];
const STATUSES = ["Uncorrected", "Corrected"];
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

export function DifferencesTable({ wp, engagementId, locked, onSaved }) {
    const [src, setSrc] = useState(null);
    const [rows, setRows] = useState((wp.data?.differences) || []);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        api.get(`/working-papers/${wp.id}/f2-differences`).then((r) => setSrc(r.data)).catch(() => setSrc({ materiality: {}, candidates: [], links: {} }));
    }, [wp.id]);
    if (!src) return null;

    const mat = src.materiality || {};
    const trivial = n(mat.trivial);
    const pm = n(mat.performance);
    const overall = n(mat.overall);
    const postedKeys = new Set(rows.map((r) => r.key).filter(Boolean));
    const suggestions = (src.candidates || []).filter((c) => !postedKeys.has(c.key));

    const update = (id, patch) => { setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r))); setDirty(true); };
    const remove = (id) => { setRows((rs) => rs.filter((r) => r.id !== id)); setDirty(true); };
    const addManual = () => { setRows((rs) => [...rs, { id: uid(), source: "Manual", description: "", account: "", amount: "", effect: "Dr", type: "Factual", status: "Uncorrected" }]); setDirty(true); };
    const addCandidate = (c) => {
        setRows((rs) => [...rs, { id: uid(), key: c.key, source: c.wp_code, ref_wp_id: c.wp_id, step_id: c.step_id, description: `${c.step_title} — ${c.label}: ${c.value}`.trim(), account: "", amount: "", effect: "Dr", type: "Factual", status: "Uncorrected" }]);
        setDirty(true);
    };

    const isMon = (r) => r.type !== "Qualitative";
    const belowTrivial = (r) => isMon(r) && trivial > 0 && n(r.amount) > 0 && n(r.amount) < trivial;
    const included = (r) => r.status === "Uncorrected" && isMon(r) && !belowTrivial(r);
    const signed = (r) => (r.effect === "Cr" ? 1 : -1) * n(r.amount);

    const uncorrRows = rows.filter(included);
    const netUncorr = uncorrRows.reduce((a, r) => a + signed(r), 0);
    const grossUncorr = uncorrRows.reduce((a, r) => a + n(r.amount), 0);
    const correctedTotal = rows.filter((r) => r.status === "Corrected" && isMon(r)).reduce((a, r) => a + n(r.amount), 0);
    const belowCount = rows.filter(belowTrivial).length;
    const qualCount = rows.filter((r) => r.type === "Qualitative").length;
    const absNet = Math.abs(netUncorr);
    const overPm = pm > 0 && absNet > pm;
    const overOverall = overall > 0 && absNet > overall;

    const save = async () => {
        try { await api.patch(`/working-papers/${wp.id}/data`, { differences: rows }); setDirty(false); toast.success("Differences schedule saved"); onSaved?.(); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const link = (code, id) => id
        ? <a href={`/engagements/${engagementId}/wp/${id}`} target="_blank" rel="noreferrer" className="text-brand font-num font-semibold underline decoration-dotted" data-testid={`f2-ref-${code}`}>{code}</a>
        : <span className="font-num text-muted-foreground">{code}</span>;
    const sel = "border border-input rounded-sm px-1.5 py-1 text-xs bg-white disabled:bg-slate-50";

    return (
        <div className="bg-white border-2 border-brand/40 rounded-sm mb-5" data-testid="differences-table">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
                <Scale className="w-4 h-4 text-brand" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-display font-semibold">Schedule of audit differences · compared with materiality</span>
                <span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-1"><Link2 className="w-3 h-3" /> Exceptions from B01, C–E, F0, F3, F4 post here</span>
            </div>

            {/* Materiality context */}
            <div className="px-4 py-2.5 border-b border-border grid grid-cols-3 gap-3 text-xs" data-testid="f2-materiality-bar">
                <MatCell label="Overall materiality" value={overall} present={mat.overall != null} />
                <MatCell label="Performance materiality" value={pm} present={mat.performance != null} />
                <MatCell label="Clearly trivial threshold" value={trivial} present={mat.trivial != null} />
            </div>
            {mat.overall == null && <div className="mx-4 my-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-1.5 flex items-center gap-2" data-testid="f2-no-materiality"><AlertTriangle className="w-3.5 h-3.5" /> Set materiality on A02 to enable the comparison.</div>}

            {/* Suggested candidates */}
            {suggestions.length > 0 && (
                <div className="px-4 py-2.5 border-b border-border" data-testid="f2-candidates">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display font-semibold mb-1.5">Exceptions flagged elsewhere · {suggestions.length} to consider</div>
                    <div className="grid gap-1.5">
                        {suggestions.map((c) => (
                            <div key={c.key} className="flex items-start gap-2 text-xs border border-dashed border-border rounded-sm px-2.5 py-1.5" data-testid={`f2-candidate-${c.wp_code}`}>
                                <span className="font-num font-semibold text-brand shrink-0">{c.wp_code}</span>
                                <span className="flex-1 min-w-0"><span className="font-medium">{c.step_title}</span> — {c.label}: <span className="text-muted-foreground">{c.value}</span></span>
                                {!locked && <button data-testid={`f2-add-candidate-${c.wp_code}`} onClick={() => addCandidate(c)} className="shrink-0 text-xs text-brand hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add to schedule</button>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Rows */}
            <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="f2-rows">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left font-semibold px-3 py-1.5 w-16">Ref</th>
                            <th className="text-left font-semibold px-3 py-1.5">Description</th>
                            <th className="text-left font-semibold px-3 py-1.5 w-32">Account</th>
                            <th className="text-right font-semibold px-3 py-1.5 w-28">£ effect</th>
                            <th className="text-left font-semibold px-2 py-1.5 w-16">Dr/Cr</th>
                            <th className="text-left font-semibold px-2 py-1.5 w-28">Type</th>
                            <th className="text-left font-semibold px-2 py-1.5 w-28">Status</th>
                            <th className="w-8" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-center text-muted-foreground italic">No differences recorded. Add flagged exceptions above or a manual row below.</td></tr>}
                        {rows.map((r) => {
                            const bt = belowTrivial(r);
                            return (
                                <tr key={r.id} className={`border-b border-dashed border-border/70 ${bt ? "opacity-60" : ""}`} data-testid={`f2-row-${r.id}`}>
                                    <td className="px-3 py-1.5 align-top">{link(r.source, r.ref_wp_id)}</td>
                                    <td className="px-3 py-1.5 align-top">
                                        <textarea data-testid="f2-desc" disabled={locked} rows={1} value={r.description} onChange={(e) => update(r.id, { description: e.target.value })} className="w-full border border-input rounded-sm px-2 py-1 text-xs bg-white disabled:bg-slate-50 resize-y" placeholder="Nature of the difference" />
                                        {bt && <span className="text-[10px] text-amber-700">below trivial — excluded from total</span>}
                                    </td>
                                    <td className="px-3 py-1.5 align-top"><input data-testid="f2-account" disabled={locked} value={r.account} onChange={(e) => update(r.id, { account: e.target.value })} className="w-full border border-input rounded-sm px-2 py-1 text-xs bg-white disabled:bg-slate-50" /></td>
                                    <td className="px-3 py-1.5 align-top"><input data-testid="f2-amount" disabled={locked || r.type === "Qualitative"} value={r.type === "Qualitative" ? "" : r.amount} onChange={(e) => update(r.id, { amount: e.target.value })} placeholder={r.type === "Qualitative" ? "n/a" : "£"} className="w-full border border-input rounded-sm px-2 py-1 text-xs font-num text-right bg-white disabled:bg-slate-50" /></td>
                                    <td className="px-2 py-1.5 align-top"><select data-testid="f2-effect" disabled={locked || r.type === "Qualitative"} value={r.effect} onChange={(e) => update(r.id, { effect: e.target.value })} className={sel}><option>Dr</option><option>Cr</option></select></td>
                                    <td className="px-2 py-1.5 align-top"><select data-testid="f2-type" disabled={locked} value={r.type} onChange={(e) => update(r.id, { type: e.target.value })} className={sel}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></td>
                                    <td className="px-2 py-1.5 align-top"><select data-testid="f2-status" disabled={locked} value={r.status} onChange={(e) => update(r.id, { status: e.target.value })} className={sel}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></td>
                                    <td className="px-1 py-1.5 align-top">{!locked && <button data-testid="f2-del-row" onClick={() => remove(r.id)} className="text-muted-foreground hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {!locked && <div className="px-3 py-2 border-b border-border"><button data-testid="f2-add-manual" onClick={addManual} className="text-xs text-brand hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add manual difference</button></div>}

            {/* Totals & assessment */}
            <div className="px-4 py-3 grid gap-1.5 text-xs" data-testid="f2-totals">
                <TotalRow label="Corrected differences (agreed by management)" value={fmtGbp(correctedTotal)} />
                <TotalRow label="Uncorrected differences — gross" value={fmtGbp(grossUncorr)} />
                <TotalRow label={<b>Uncorrected differences — net effect on surplus/deficit</b>} value={<b className={overPm ? "text-rose-700" : "text-emerald-700"}>{netUncorr >= 0 ? "" : "("}{fmtGbp(absNet)}{netUncorr >= 0 ? " Cr" : ") Dr"}</b>} strong />
                {belowCount > 0 && <div className="text-[11px] text-muted-foreground">{belowCount} item{belowCount > 1 ? "s" : ""} below the trivial threshold excluded from the total.</div>}
                {qualCount > 0 && <div className="text-[11px] text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-600" /> {qualCount} qualitative matter{qualCount > 1 ? "s" : ""} recorded — evaluate regardless of amount.</div>}
            </div>

            {overall > 0 && (
                <div className="px-4 pb-3 grid grid-cols-2 gap-3" data-testid="f2-assessment">
                    <Assess label="vs performance materiality" over={overPm} amount={pm} present={pm > 0} />
                    <Assess label="vs overall materiality" over={overOverall} amount={overall} present={overall > 0} />
                </div>
            )}

            {!locked && <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-3">
                {dirty && <span className="text-[11px] text-amber-700">Unsaved changes</span>}
                <button data-testid="f2-save-btn" onClick={save} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-1.5 rounded-sm">Save differences schedule</button>
            </div>}
        </div>
    );
}

function MatCell({ label, value, present }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-num font-semibold text-sm">{present ? fmtGbp(value) : "—"}</div>
        </div>
    );
}
function TotalRow({ label, value, strong }) {
    return <div className={`flex items-center gap-3 py-1 border-t border-dashed border-border/70 first:border-0 ${strong ? "bg-slate-50/60 -mx-4 px-4" : ""}`}><div className="flex-1">{label}</div><div className="font-num text-right w-40">{value}</div></div>;
}
function Assess({ label, over, amount, present }) {
    if (!present) return <div className="text-[11px] text-muted-foreground">{label}: not set on A02</div>;
    return (
        <div className={`rounded-sm px-3 py-2 flex items-center gap-2 border ${over ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid={`f2-assess-${over ? "over" : "within"}`}>
            {over ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
            <div className="text-xs"><div className="font-semibold">{over ? "Exceeds" : "Within"} {label}</div><div className="font-num">{fmtGbp(amount)}</div></div>
        </div>
    );
}
